import { Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { GuideLinks } from "@/components/site/Guides";
import { InlineCta } from "@/components/site/InlineCta";
import { SchoolMap } from "@/components/site/SchoolMap";
import type { AreaContent, AreaSection } from "@/content/areas";
import { pathFor } from "@/lib/i18n";

type Locale = "sv" | "en";

function Sections({ sections }: { sections: AreaSection[] }) {
  return (
    <>
      {sections.map((section) => (
        <div key={section.heading}>
          <h2>{section.heading}</h2>
          {section.paragraphs.map((text) => (
            <p key={text}>{text}</p>
          ))}
          {section.bullets ? (
            <ul>
              {section.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </>
  );
}

/**
 * Delad layout för de lokala områdessidorna.
 * Allt innehåll kommer från src/content/areas.ts – komponenten är bara presentation.
 */
export function AreaPage({
  locale,
  content,
  currentSvPath,
}: {
  locale: Locale;
  content: AreaContent;
  currentSvPath: string;
}) {
  const t = content.travelLabels;

  return (
    <>
      <PageHero
        eyebrow={content.eyebrow}
        title={content.title}
        intro={content.intro}
      />

      <Prose>
        <Sections sections={content.sectionsBeforeMap} />
      </Prose>

      <section className="mx-auto max-w-3xl px-5 pb-4">
        <h2 className="text-2xl font-semibold">{content.travelHeading}</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{content.travelNote}</p>

        <div className="mt-6 overflow-hidden rounded-xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-sand text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t.from}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t.distance}
                </th>
                <th scope="col" className="hidden px-4 py-3 font-semibold sm:table-cell">
                  {t.calm}
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  {t.rush}
                </th>
              </tr>
            </thead>
            <tbody>
              {content.travel.map((row) => (
                <tr key={row.from} className="border-t border-border">
                  <th scope="row" className="px-4 py-3 text-left font-medium text-foreground">
                    {row.from}
                  </th>
                  <td className="px-4 py-3 text-foreground/80">{row.distance}</td>
                  <td className="hidden px-4 py-3 text-foreground/80 sm:table-cell">{row.calm}</td>
                  <td className="px-4 py-3 text-foreground/80">{row.rush}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8">
          <SchoolMap locale={locale} mapClassName="h-[320px] lg:h-[420px]" />
        </div>
      </section>

      <Prose>
        <Sections sections={content.sectionsAfterMap} />
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale={locale} />
      </div>

      <section className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
        <h2 className="text-2xl font-semibold">{content.faqHeading}</h2>
        <dl className="mt-6 space-y-5">
          {content.faq.map((item) => (
            <div key={item.question} className="rounded-xl border border-border bg-card p-5">
              <dt className="text-base font-semibold text-foreground">{item.question}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-foreground/85">{item.answer}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 text-sm text-muted-foreground">
          <Link
            className="font-medium text-primary link-quiet"
            to={pathFor("/fragor-och-svar", locale) as never}
          >
            {locale === "en" ? "See all questions and answers" : "Se alla frågor och svar"}
          </Link>
        </p>
      </section>

      <GuideLinks current={currentSvPath} locale={locale} />
      <CtaBand locale={locale} />
      <div className="h-20" />
    </>
  );
}
